import { useCallback, useEffect } from 'react';

import { di } from 'react-magnetic-di';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type { CloseLiveChatWithMutation } from '../../common/utils/extension-components-context/types';
import { AI_WIDGET_STORE_UFO_EXPERIENCES } from '../../controllers/chat-store/experiences';
import { useChatStore, useChatStoreActions } from '../../controllers/chat-store/main';
import type { OmniEnhancedConversation } from '../../controllers/chat-store/types';

import { useOcgClient } from './use-ocg-client';

/**
 * TwilioLiveChatCloseProvider
 *
 * Component that exposes a closeLiveChatWithMutation static method for ending
 * a Twilio live chat conversation. Follows the same pattern as
 * OmniChannelLiveChatCloseProvider so that React hooks can be called within
 * the close handler.
 *
 * Consumers access the close function via the extension components system:
 * useExtensionComponent('liveChatCloseProvider')?.closeLiveChatWithMutation
 */
export const TwilioLiveChatCloseProvider: {
	(): null;
	closeLiveChatWithMutation: CloseLiveChatWithMutation | null;
} = () => {
	di(useChatStore, useChatStoreActions, useOcgClient);

	const [{ currentConversationId, conversations, csmLiveChatSubscriptionId }] = useChatStore();
	const { cleanupTwilioConversation } = useChatStoreActions();
	const ocgClient = useOcgClient();

	const closeLiveChatWithMutation = useCallback(async () => {
		// Call the dedicated close endpoint so the backend ends the live chat session
		const twilioConversationSid =
			(conversations[currentConversationId] as OmniEnhancedConversation | undefined)
				?.twilioConversationId ?? null;
		if (!twilioConversationSid) {
			return;
		}

		// FE observability (BEARS-1036): "Close a live chat" success event for the
		// underlying `ocgClient.closeTwilioConversation` API call. Uses the
		// dedicated `END_TWILIO_LIVE_CHAT` experience (distinct from the
		// user-click-driven `EXIT_LIVE_CHAT` instance owned by
		// `LiveChatExitButton`) so the two paths can be charted independently.
		const ufoExperience =
			AI_WIDGET_STORE_UFO_EXPERIENCES.END_TWILIO_LIVE_CHAT.getInstance(twilioConversationSid);
		ufoExperience?.start();

		try {
			await ocgClient.closeTwilioConversation(twilioConversationSid);
			ufoExperience?.success();
		} catch (error) {
			ufoExperience?.failure();
			logException({
				exception: error,
				name: 'CloseTwilioConversationFailed',
			});
		}

		// Clean up Twilio resources (token refresh, client, state, localStorage)
		try {
			await cleanupTwilioConversation(currentConversationId);
		} catch (error) {
			logException({
				exception: error,
				name: 'TwilioConversationCleanupFailed',
			});
		}
	}, [ocgClient, cleanupTwilioConversation, conversations, currentConversationId]);

	useEffect(() => {
		if (csmLiveChatSubscriptionId) {
			TwilioLiveChatCloseProvider.closeLiveChatWithMutation = closeLiveChatWithMutation;
		} else {
			TwilioLiveChatCloseProvider.closeLiveChatWithMutation = null;
		}

		return () => {
			TwilioLiveChatCloseProvider.closeLiveChatWithMutation = null;
		};
	}, [closeLiveChatWithMutation, csmLiveChatSubscriptionId]);

	return null;
};

TwilioLiveChatCloseProvider.closeLiveChatWithMutation = null as CloseLiveChatWithMutation | null;
